import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import {
  EnabledStatusReportTemplateKeysResponseSchema,
  ListStatusReportTemplatesResponseSchema,
  StatusReportTemplateSchema,
} from "@forgepoint/shared";
import type {
  StatusReportTemplate,
  StatusReportTemplateKey,
} from "@forgepoint/shared";

/** Per-template loading/error flags, keyed by template key. */
export interface TemplateLoadingState {
  isLoadingSave: boolean;
  saveError: string | null;
}

interface StatusReportTemplatesStore {
  /** Map of templateKey -> template, kept current across mutations. */
  templateMap: Map<StatusReportTemplateKey, StatusReportTemplate>;
  /** Per-template save loading states, keyed by template key. */
  loadingByKey: Record<string, TemplateLoadingState>;
  isLoadingList: boolean;
  error: string | null;
  /** The org's effectively enabled template keys; null until fetched. */
  enabledTemplateKeys: StatusReportTemplateKey[] | null;

  fetchTemplates: () => Promise<void>;
  /** Fetches the enabled template keys (member-accessible, no settings.edit). */
  fetchEnabledTemplateKeys: () => Promise<void>;
  /** Flips one template's enabled toggle, leaving its settings untouched. */
  setTemplateEnabled: (
    templateKey: StatusReportTemplateKey,
    enabled: boolean,
  ) => Promise<StatusReportTemplate | null>;
  /**
   * Saves an edited template row. Takes the whole discriminated row, so its key
   * and settings travel together and cannot be mismatched by a caller.
   */
  saveTemplate: (
    template: StatusReportTemplate,
    reportContextBlurb: string | null,
  ) => Promise<StatusReportTemplate | null>;
}

/**
 * What one PUT changes: the toggle alone, or the settings and blurb of a row
 * the caller already holds. Settings arrive attached to their own row, so the
 * key they belong to is never in question.
 */
type TemplatePatch =
  | { enabled: boolean }
  | {
      settings: StatusReportTemplate["settings"];
      reportContextBlurb: string | null;
    };

const IDLE_LOADING_STATE: TemplateLoadingState = {
  isLoadingSave: false,
  saveError: null,
};

/**
 * Store of the org's status-report templates (keyed by template key), backed by
 * the /status-report-templates REST endpoints. The template rows require
 * settings.edit (the surface consuming them is hidden without it); the
 * enabled-keys read is member-accessible and feeds the personal report picker.
 */
export const useStatusReportTemplatesStore = create<StatusReportTemplatesStore>(
  (set) => ({
    templateMap: new Map(),
    loadingByKey: {},
    isLoadingList: false,
    error: null,
    enabledTemplateKeys: null,

    fetchTemplates: async () => {
      set({ isLoadingList: true, error: null });
      try {
        const response = await fetchApi("status-report-templates", "GET");
        if (response.ok && response.data) {
          const data = ListStatusReportTemplatesResponseSchema.parse(
            response.data,
          );
          set({
            templateMap: new Map(data.templates.map((t) => [t.templateKey, t])),
            isLoadingList: false,
          });
          return;
        }
        set({
          error: response.error ?? "Failed to fetch report templates",
          isLoadingList: false,
        });
      } catch (error) {
        captureException(error as Error);
        set({
          error: "Failed to fetch report templates",
          isLoadingList: false,
        });
      }
    },

    fetchEnabledTemplateKeys: async () => {
      // Left null on any failure; consumers fall back to offering every template.
      try {
        const response = await fetchApi(
          "status-report-templates/enabled-keys",
          "GET",
        );
        if (response.ok && response.data) {
          const data = EnabledStatusReportTemplateKeysResponseSchema.parse(
            response.data,
          );
          set({ enabledTemplateKeys: data.templateKeys });
        }
      } catch (error) {
        captureException(error as Error);
      }
    },

    setTemplateEnabled: async (templateKey, enabled) =>
      putTemplate(set, templateKey, { enabled }),

    saveTemplate: async (template, reportContextBlurb) =>
      putTemplate(set, template.templateKey, {
        settings: template.settings,
        reportContextBlurb,
      }),
  }),
);

/** Issues one template's PUT and folds the updated row back into the store. */
async function putTemplate(
  set: (
    partial: (
      state: StatusReportTemplatesStore,
    ) => Partial<StatusReportTemplatesStore>,
  ) => void,
  templateKey: StatusReportTemplateKey,
  patch: TemplatePatch,
): Promise<StatusReportTemplate | null> {
  setTemplateLoading(set, templateKey, {
    isLoadingSave: true,
    saveError: null,
  });
  try {
    const response = await fetchApi(
      `status-report-templates/${templateKey}`,
      "PUT",
      { body: patch },
    );
    if (response.ok && response.data) {
      const template = StatusReportTemplateSchema.parse(response.data);
      set((state) => ({
        templateMap: new Map(state.templateMap).set(
          template.templateKey,
          template,
        ),
      }));
      setTemplateLoading(set, templateKey, { isLoadingSave: false });
      return template;
    }
    setTemplateLoading(set, templateKey, {
      isLoadingSave: false,
      saveError: response.error ?? "Failed to update report template",
    });
    return null;
  } catch (error) {
    captureException(error as Error);
    setTemplateLoading(set, templateKey, {
      isLoadingSave: false,
      saveError: "Failed to update report template",
    });
    return null;
  }
}

/** Merges a partial loading state for one template key. */
function setTemplateLoading(
  set: (
    fn: (
      state: StatusReportTemplatesStore,
    ) => Partial<StatusReportTemplatesStore>,
  ) => void,
  templateKey: string,
  partial: Partial<TemplateLoadingState>,
): void {
  set((state) => ({
    loadingByKey: {
      ...state.loadingByKey,
      [templateKey]: {
        ...(state.loadingByKey[templateKey] ?? IDLE_LOADING_STATE),
        ...partial,
      },
    },
  }));
}
